import { useSelector } from 'react-redux';

import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { selectIntegrationId } from '@/redux/session/selectors';
import { IntegrationStatus, TwilioVoiceIntegration } from '@/shared/types/integration';

import IntegrationConfigWrapper from '../Components/Integration';
import Account from './cards/Account';
import TranscriptRelay from './cards/TranscriptRelay';
import AdvancedSettings from '../VoiceCommon/cards/AdvancedSettings';
import AnsweringMachineDetection from '../VoiceCommon/cards/AnsweringMachineDetection';
import Configuration from '../VoiceCommon/cards/Configuration';
import NoiseSuppression from '../VoiceCommon/cards/NoiseSuppression';
import Preferences from '../VoiceCommon/cards/Preferences';
import SipCaller from '../VoiceCommon/cards/SipCaller';
import Transcription from '../VoiceCommon/cards/Transcription';
import Voice from '../VoiceCommon/cards/Voice';
import VoiceIntegrationSkeleton from '../VoiceCommon/cards/VoiceIntegrationSkeleton';

/**
 * Twilio Voice integration configuration component
 *
 * Displays configuration settings for Twilio Voice integration including:
 * - Account settings (credentials, phone number, language)
 * - Preferences (call recording, AI agent starts first, noise suppression, AMD)
 * - Transcription settings (model, prompt, turn detection)
 * - Voice settings (voice selection, TTS model, voice parameters)
 */
const IntegrationTwilioVoice = () => {
  const integrationId = useSelector(selectIntegrationId);
  const deskId = useDeskId();
  const { integration, getStatus } = useIntegrations<TwilioVoiceIntegration>(deskId, integrationId);

  if (getStatus === 'pending' || !integration) {
    return <VoiceIntegrationSkeleton />;
  }

  return (
    <IntegrationConfigWrapper integration={integration} className="gap-12">
      <Account integration={integration} />
      <Preferences>
        <NoiseSuppression />
        <AnsweringMachineDetection />
      </Preferences>
      <Configuration showLanguageField={integration.status === IntegrationStatus.VERIFIED} />
      <AdvancedSettings>
        <Transcription />
        <Voice />
        <TranscriptRelay />
        <SipCaller />
      </AdvancedSettings>
    </IntegrationConfigWrapper>
  );
};

export default IntegrationTwilioVoice;
